import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { visit } from 'unist-util-visit';
import remarkDirective from 'remark-directive';
import { pipe } from '@support/operators';
import styles from './Markdown.module.scss';
import { HighlightItem } from '@models/HighlightItem';

// Custom parsed entities
const renderAdded = ({ children }) => (
  <span className={`${styles.added} diff-added`}>{children}</span>
);

const renderRemoved = ({ children }) => (
  <span className={`${styles.removed} diff-removed`}>{children}</span>
);

const renderOrderedList = ({ children, ...props }) => (
  <p>
    {props.start ? props.start : '1'}. {children}
  </p>
);

const renderLI = ({ children }) => <>{children}</>;

const remarkClay = () => (tree) => {
  visit(tree, ['textDirective'], (node) => {
    node.data = {
      hName: node.name,
      hProperties: node.attributes,
      ...node.data,
    };
    return node;
  });
};

const renderDiffLink = ({ children, ...props }) => (
  <a onClick={(e) => e.stopPropagation()} {...props}>
    <span className={styles.link}>{children}</span>
  </a>
);

export function renderDiffMarkdown(content: string) {
  return (
    <ReactMarkdown
      children={content}
      linkTarget="_blank"
      remarkPlugins={[remarkGfm, remarkDirective, remarkClay]}
      components={{
        a: renderDiffLink,
        ol: renderOrderedList,
        li: renderLI,
        added: renderAdded,
        removed: renderRemoved,
      }}
    />
  );
}

export default function renderSharedContactHighlightsMarkdown(item: HighlightItem) {
  const useCustomParsing = item.source == 'twitter';
  const remarkPlugins = useCustomParsing ? [remarkGfm, remarkDirective, remarkClay] : [remarkGfm];

  return (
    <ReactMarkdown
      children={item.content}
      linkTarget="_blank"
      components={{
        a: renderDiffLink,
        added: renderAdded,
        removed: renderRemoved,
      }}
      remarkPlugins={remarkPlugins}
    />
  );
}

const mapDelimiterToShortcode = (delimiter: string, shortcodeType: string) => {
  const escapedDelimiter = '\\' + delimiter.split('').join('\\');
  const pattern = `${escapedDelimiter}.+?${escapedDelimiter}`;
  const regex = RegExp(pattern);
  const delimLength = delimiter.length;

  return (md: string) => {
    let mdWithReplacements = md;
    let lastMatch = null;

    while ((lastMatch = regex.exec(mdWithReplacements)) !== null) {
      const match = lastMatch[0];
      const replacement = `:${shortcodeType}[${match.slice(delimLength, -delimLength)}]`;
      const idx = lastMatch.index;
      const beforeMatch = mdWithReplacements.slice(0, idx);
      const afterMatch = mdWithReplacements.slice(idx + match.length);
      mdWithReplacements = beforeMatch + '&#x200B;' + replacement + afterMatch;
    }

    return mdWithReplacements;
  };
};

const insertRemoved = mapDelimiterToShortcode('~~', 'removed');
const insertAdded = mapDelimiterToShortcode('**', 'added');
export const preProcessDiffMD = (md: string): string => pipe(insertRemoved, insertAdded)(md);
